import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/Conversations/ConversationComposer.tsx
import { useId, useLayoutEffect, useRef, useState } from "react";
import { MdAttachFile, MdClose, MdDescription, MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { checkAttachments, formatBytes } from "../../../domain/attachments/index.mjs";
import { usePointerDrag } from "../../../hooks/usePointerDrag.mjs";
import { isImeComposing } from "../../../utils/isImeComposing.mjs";
import { Button, ImageFrame, Spinner, TextField } from "../../primitive/index.mjs";
import { createComposerDraft } from "./composer-draft.mjs";
import { useConversationsTranslation } from "./use-conversations-translation.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var MAX_LENGTH = 1600;
function ConversationComposer({ aiPaused, blockedNotice, emailReply, draft, onDraftChange, onSend, onResumeAi, attachmentPolicy, onUploadAttachment }) {
  const { t, i18n } = useConversationsTranslation();
  const [localDraft, setLocalDraft] = useState(createComposerDraft);
  const { text, attachments, busy, uploading, sent, attachmentError } = draft ?? localDraft;
  const updateDraft = onDraftChange ?? setLocalDraft;
  const [expanded, setExpanded] = useState(false);
  const [inputHeight, setInputHeight] = useState(null);
  const [range, setRange] = useState(null);
  const limitId = useId();
  const inputId = useId();
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const composerRef = useRef(null);
  const minInputHeightRef = useRef(null);
  const draggedRef = useRef(false);
  const startDrag = usePointerDrag();
  const canAttach = Boolean(attachmentPolicy && onUploadAttachment);
  const hasContent = text.trim().length > 0 || attachments.length > 0;
  const blocked = !!blockedNotice;
  const locked = !!busy || uploading > 0 || blocked;
  const sendDisabled = locked || !hasContent;
  const attachLabel = emailReply ? t("shared.chat.reply.attach_files") : t("shared.chat.reply.attach");
  const resizeRange = () => {
    const textarea = textareaRef.current;
    const composer = composerRef.current;
    if (!textarea || !composer) return null;
    const styles = getComputedStyle(textarea);
    const height = textarea.getBoundingClientRect().height;
    minInputHeightRef.current ??= height;
    const min = minInputHeightRef.current;
    const maxHeight = Number.parseFloat(styles.maxHeight);
    const inputLimit = Number.isFinite(maxHeight) ? maxHeight : height;
    const available = composer.parentElement?.getBoundingClientRect().height ?? 0;
    const max = Math.max(min, Math.min(inputLimit, available ? available * 0.7 - (composer.getBoundingClientRect().height - height) : inputLimit));
    return { height, min, max };
  };
  useLayoutEffect(() => {
    if (expanded) return;
    const measure = () => {
      const next = resizeRange();
      if (!next) return;
      const rounded = { height: Math.round(next.height), min: Math.round(next.min), max: Math.round(next.max) };
      setRange((current) => current?.height === rounded.height && current.min === rounded.min && current.max === rounded.max ? current : rounded);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    for (const element of [textareaRef.current, composerRef.current?.parentElement]) {
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [expanded]);
  const handleResizeStart = (event) => {
    const range2 = resizeRange();
    if (!range2) return;
    const previous = inputHeight;
    const started = startDrag(event, {
      onMove: (_deltaX, deltaY) => {
        if (Math.abs(deltaY) <= 2) return;
        draggedRef.current = true;
        setInputHeight(Math.max(range2.min, Math.min(range2.max, range2.height - deltaY)));
      },
      onEnd: (reason) => {
        if (reason === "escape") {
          setInputHeight(previous);
          draggedRef.current = true;
        } else if (reason === "cancel") {
          draggedRef.current = false;
        }
      }
    });
    if (started) draggedRef.current = false;
  };
  const handleResizeClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    const range2 = resizeRange();
    if (range2) setInputHeight(range2.height > range2.min + 8 ? range2.min : range2.max);
  };
  const handleResizeKey = (event) => {
    const range2 = resizeRange();
    if (!range2) return;
    if (event.key === "Enter") {
      event.preventDefault();
      setInputHeight(range2.height > range2.min + 8 ? range2.min : range2.max);
      return;
    }
    const next = event.key === "ArrowUp" ? Math.min(range2.max, range2.height + 16) : event.key === "ArrowDown" ? Math.max(range2.min, range2.height - 16) : event.key === "Home" ? range2.min : event.key === "End" ? range2.max : null;
    if (next === null) return;
    event.preventDefault();
    setInputHeight(next);
  };
  const handleSend = async () => {
    if (sendDisabled) return;
    updateDraft((current) => ({ ...current, busy: "send", sent: false }));
    try {
      if (await onSend(text.trim(), attachments.map((attachment) => attachment.fileID))) {
        updateDraft((current) => ({ ...current, text: "", attachments: [], attachmentError: null, sent: true }));
      }
    } catch {
      return;
    } finally {
      updateDraft((current) => ({ ...current, busy: null }));
    }
  };
  const handleResume = async () => {
    if (locked) return;
    updateDraft((current) => ({ ...current, busy: "resume" }));
    try {
      await onResumeAi?.();
    } catch {
      return;
    } finally {
      updateDraft((current) => ({ ...current, busy: null }));
    }
  };
  const handlePicked = async (event) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!attachmentPolicy || !onUploadAttachment || picked.length === 0 || locked) return;
    const error = checkAttachments(picked, attachmentPolicy, attachments);
    if (error) {
      updateDraft((current) => ({ ...current, attachmentError: error }));
      return;
    }
    updateDraft((current) => ({ ...current, uploading: picked.length, attachmentError: null, sent: false }));
    for (const file of picked) {
      try {
        const stored = await onUploadAttachment(file);
        if (stored) updateDraft((current) => ({ ...current, attachments: [...current.attachments, { ...stored, fileName: stored.fileName || file.name }] }));
      } catch {
        continue;
      } finally {
        updateDraft((current) => ({ ...current, uploading: current.uploading - 1 }));
      }
    }
  };
  const attachmentErrorText = attachmentError && attachmentPolicy ? {
    unsupported_type: emailReply ? t("shared.chat.reply.attachment_error.unsupported_file_type") : t("shared.chat.reply.attachment_error.unsupported_type"),
    too_many: t("shared.chat.reply.attachment_error.too_many", { count: attachmentPolicy.maxCount }),
    too_large: t("shared.chat.reply.attachment_error.too_large", { limit: formatBytes(attachmentPolicy.maxTotalBytes, i18n.language) })
  }[attachmentError] : null;
  const attachControl = canAttach && /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("input", { ref: fileInputRef, type: "file", "aria-label": attachLabel, accept: attachmentPolicy?.accept.join(","), multiple: (attachmentPolicy?.maxCount ?? 0) > 1, hidden: true, onChange: (event) => void handlePicked(event) }),
    /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "borderless", color: "neutral", className: emailReply ? "bs-conversations__attach-full" : void 0, iconOnly: !emailReply, "aria-label": attachLabel, title: attachLabel, disabled: locked, onClick: () => fileInputRef.current?.click(), children: [
      /* @__PURE__ */ jsx("span", { className: "buyscout-btn__icon-start", children: /* @__PURE__ */ jsx(MdAttachFile, { "aria-hidden": true }) }),
      emailReply && attachLabel
    ] }),
    emailReply && /* @__PURE__ */ jsx(Button, { className: "bs-conversations__attach-compact", size: "sm", variant: "borderless", color: "neutral", iconOnly: true, "aria-label": attachLabel, title: attachLabel, disabled: locked, onClick: () => fileInputRef.current?.click(), children: /* @__PURE__ */ jsx("span", { className: "buyscout-btn__icon-start", children: /* @__PURE__ */ jsx(MdAttachFile, { "aria-hidden": true }) }) })
  ] });
  return /* @__PURE__ */ jsxs("div", { ref: composerRef, className: "bs-conversations__composer", "data-email": !!emailReply, "data-expanded": expanded, style: { "--bs-conversations-input-height": inputHeight === null ? void 0 : `${inputHeight}px` }, children: [
    !expanded && /* @__PURE__ */ jsx(
      "div",
      {
        role: "separator",
        tabIndex: 0,
        className: "bs-conversations__resize-handle",
        "aria-orientation": "horizontal",
        "aria-controls": inputId,
        "aria-label": t("shared.chat.reply.resize"),
        "aria-valuenow": range?.height ?? 0,
        "aria-valuemin": range?.min ?? 0,
        "aria-valuemax": range?.max ?? 0,
        "aria-valuetext": range?.max ? t("shared.chat.reply.resize_value", { percent: Math.round(range.height / range.max * 100) }) : void 0,
        title: t("shared.chat.reply.resize_hint"),
        onPointerDown: handleResizeStart,
        onClick: handleResizeClick,
        onKeyDown: handleResizeKey
      }
    ),
    emailReply && /* @__PURE__ */ jsxs("div", { className: "bs-conversations__compose-header", children: [
      /* @__PURE__ */ jsxs("div", { className: "bs-conversations__reply-context", children: [
        /* @__PURE__ */ jsx("strong", { children: t("shared.chat.reply.heading") }),
        emailReply.recipient && /* @__PURE__ */ jsx("span", { children: t("shared.chat.reply.to", { recipient: emailReply.recipient }) }),
        emailReply.subject && (expanded || emailReply.showSubject) && /* @__PURE__ */ jsx("span", { className: "bs-conversations__reply-subject", children: emailReply.subject })
      ] }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "borderless", color: "neutral", iconOnly: true, "aria-label": expanded ? t("shared.chat.reply.collapse") : t("shared.chat.reply.expand"), title: expanded ? t("shared.chat.reply.collapse") : t("shared.chat.reply.expand"), "aria-expanded": expanded, onClick: () => setExpanded((value) => !value), children: expanded ? /* @__PURE__ */ jsx(MdFullscreenExit, { "aria-hidden": true }) : /* @__PURE__ */ jsx(MdFullscreen, { "aria-hidden": true }) })
    ] }),
    sent && /* @__PURE__ */ jsx("p", { role: "status", children: t("shared.chat.reply.sent") }),
    blockedNotice && /* @__PURE__ */ jsx("p", { role: "status", className: "bs-conversations__blocked-notice", children: blockedNotice }),
    aiPaused && !blocked && onResumeAi && /* @__PURE__ */ jsxs("div", { className: "bs-conversations__ai-notice", children: [
      /* @__PURE__ */ jsx("span", { children: t("shared.chat.reply.ai_paused") }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outlined", color: "neutral", loading: busy === "resume", disabled: busy !== "resume" && locked, onClick: () => void handleResume(), children: t("shared.chat.reply.return_to_ai") })
    ] }),
    attachmentErrorText && /* @__PURE__ */ jsx("p", { role: "alert", children: attachmentErrorText }),
    /* @__PURE__ */ jsxs("div", { className: "bs-conversations__compose-fields", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          ref: textareaRef,
          id: inputId,
          className: "flex-grow",
          fullWidth: true,
          multiline: true,
          rows: 2,
          dir: "auto",
          "aria-describedby": emailReply ? limitId : void 0,
          disabled: !!busy || blocked,
          value: text,
          maxLength: MAX_LENGTH,
          "aria-label": t("shared.chat.reply.placeholder"),
          placeholder: t("shared.chat.reply.placeholder"),
          onChange: (event) => {
            const value = event.target.value;
            updateDraft((current) => ({ ...current, text: value, sent: false }));
          },
          onKeyDown: (event) => {
            if (event.key === "Enter" && !event.shiftKey && (event.metaKey || event.ctrlKey) && !isImeComposing(event.nativeEvent)) {
              event.preventDefault();
              void handleSend();
            }
          }
        }
      ),
      (attachments.length > 0 || uploading > 0) && /* @__PURE__ */ jsxs("ul", { className: "bs-conversations__pending-files", "aria-label": t("shared.chat.reply.pending_attachments"), children: [
        attachments.map((attachment) => /* @__PURE__ */ jsxs("li", { children: [
          attachment.contentType.startsWith("image/") ? /* @__PURE__ */ jsx(ImageFrame, { className: "bs-conversations__pending-image", src: attachment.url, alt: "" }) : /* @__PURE__ */ jsx(MdDescription, { "aria-hidden": true }),
          /* @__PURE__ */ jsxs("span", { className: "bs-conversations__file-info", children: [
            /* @__PURE__ */ jsx("span", { dir: "auto", children: attachment.fileName || t("shared.chat.reply.attachment") }),
            /* @__PURE__ */ jsx("small", { children: formatBytes(attachment.size, i18n.language) })
          ] }),
          /* @__PURE__ */ jsx(Button, { size: "sm", variant: "borderless", color: "neutral", iconOnly: true, "aria-label": attachment.fileName ? t("shared.chat.reply.remove_named_attachment", { name: attachment.fileName }) : t("shared.chat.reply.remove_attachment"), disabled: locked, onClick: () => updateDraft((current) => ({ ...current, attachments: current.attachments.filter((item) => item.fileID !== attachment.fileID), attachmentError: null })), children: /* @__PURE__ */ jsx(MdClose, { "aria-hidden": true }) })
        ] }, attachment.fileID)),
        uploading > 0 && /* @__PURE__ */ jsxs("li", { role: "status", children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm", "aria-hidden": true }),
          /* @__PURE__ */ jsx("span", { children: t("shared.chat.reply.uploading_files", { count: uploading }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "bs-conversations__compose-footer", children: [
        canAttach && /* @__PURE__ */ jsx("div", { className: "bs-conversations__compose-tools", children: attachControl }),
        /* @__PURE__ */ jsxs("div", { className: "bs-conversations__compose-actions", children: [
          emailReply && /* @__PURE__ */ jsxs("span", { id: limitId, className: "bs-conversations__compose-limit", children: [
            text.length.toLocaleString(),
            " / ",
            MAX_LENGTH.toLocaleString()
          ] }),
          /* @__PURE__ */ jsx(Button, { size: "sm", loading: busy === "send", disabled: busy !== "send" && sendDisabled, title: t("shared.chat.reply.send_hint"), onClick: () => void handleSend(), children: busy === "send" ? t("shared.chat.reply.sending") : t("shared.chat.reply.send") })
        ] })
      ] })
    ] })
  ] });
}
ConversationComposer.displayName = "ConversationComposer";
var ConversationComposer_default = ConversationComposer;
export {
  ConversationComposer_default as default
};
//# sourceMappingURL=ConversationComposer.mjs.map